import { IOrganisation } from "@typed/api/responses/Organisation"
import { IWorkDetails } from "@typed/api/responses/WorkDetails"
import { formatRetailPrice, getCurrencyParts, populatePriceTemplate } from "@services/pdf/priceTemplate"
import { IOrgAttachment, IPdfPageData, IPdfRow, TPdfLayoutType, TPdfPageType } from "@services/pdf/types"

/**
 * Row construction — port of iOS WorkPDFCustomPage.setupPages().
 * Rows are built AFTER org templates finish loading (iOS does the same),
 * so template bytes can live inside the page type objects.
 */

// Currently no user preference exists in the web app; iOS defaults to metric.
const preferredUnit = "metric" as const

export function workToPageData(work: IWorkDetails): IPdfPageData {
	const { symbol, code } = getCurrencyParts(work.retail_price_currency)
	return {
		id: work.id,
		artist: work.artist,
		title: work.title,
		year: work.year ?? "",
		medium: work.medium ?? "",
		price: formatRetailPrice(work.retail_price),
		currencySymbol: symbol,
		currencyCode: code,
		width: work.dim_width,
		height: work.dim_height,
		depth: work.dim_depth,
		otherDim: work.dims_other,
		// iOS ItemModel.notes decodes from the "description" JSON key
		note: work.description,
		provenance: work.provenance,
		exhibition: work.exhibition_history,
		literature: work.literature,
		edition: work.edition,
		unit: preferredUnit,
		images: (work.images ?? []).map((image) => ({ url: image.fullscreen, backupUrl: image.detail })),
	}
}

/** iOS WorkPDFPageDataModel.priceFormatted — prefill for the price override field. */
export function priceFormatted(item: IPdfPageData, org: IOrganisation | null, taxMessage: string): string {
	return populatePriceTemplate(
		org?.pdf_price_template ?? "",
		item.currencySymbol,
		item.currencyCode,
		item.price,
		taxMessage
	)
}

export interface IBuiltRows {
	coverRow: IPdfRow
	contentRows: IPdfRow[]
}

export interface IBuildRowsInput {
	works: IWorkDetails[]
	org: IOrganisation | null
	coverTemplate: Uint8Array | null
	detailsTemplate: Uint8Array | null
	attachments: IOrgAttachment[]
	taxMessage: string
}

export function buildRows(input: IBuildRowsInput): IBuiltRows {
	const { works, org, coverTemplate, detailsTemplate, attachments, taxMessage } = input
	const layoutType: TPdfLayoutType = org?.pdf_layout_type ?? ""

	const items = works.map((work) => workToPageData(work))

	// iOS getCover(): wc orgs get WCImageCover for a single work, WCCover otherwise
	const coverType: TPdfPageType =
		layoutType === "wc"
			? items.length === 1
				? { kind: "wcImageCover", templateBytes: coverTemplate }
				: { kind: "wcCover", templateBytes: coverTemplate }
			: { kind: "cover", templateBytes: coverTemplate, layoutType }

	// iOS: cover title defaults to the artist when all works share one artist
	const uniqueArtists = items.map((item) => item.artist).filter((artist, index, arr) => arr.indexOf(artist) === index)

	const coverRow: IPdfRow = {
		id: "cover",
		rowType: "cover",
		pages: [{ id: "cover-0", type: coverType, isSelected: true, imageUrl: items[0]?.images[0] ?? null }],
		customInput: uniqueArtists.length === 1 ? items[0]?.artist ?? "" : "",
	}

	const infoPageType = (item: IPdfPageData): TPdfPageType =>
		layoutType === "wc"
			? { kind: "wcImageInfo", item, templateBytes: detailsTemplate }
			: { kind: "imageInfo", item, templateBytes: detailsTemplate, layoutType }

	const contentRows: IPdfRow[] = []

	items.forEach((item, itemIndex) => {
		const prefill = priceFormatted(item, org, taxMessage)
		if (item.images.length === 0) {
			contentRows.push({
				id: `art-${itemIndex}`,
				rowType: "artwork",
				pages: [{ id: `art-${itemIndex}-info`, type: infoPageType(item), isSelected: true, imageUrl: null }],
				customInput: prefill,
				artist: item.artist,
				item,
			})
		} else {
			item.images.forEach((imageUrl, imageIndex) => {
				contentRows.push({
					id: `art-${itemIndex}-${imageIndex}`,
					rowType: "artwork",
					pages: [
						{
							id: `art-${itemIndex}-${imageIndex}-info`,
							type: infoPageType(item),
							isSelected: imageIndex === 0,
							imageUrl,
						},
						{
							id: `art-${itemIndex}-${imageIndex}-fill`,
							type: { kind: "image", fill: true },
							isSelected: false,
							imageUrl,
						},
						{
							id: `art-${itemIndex}-${imageIndex}-fit`,
							type: { kind: "image", fill: false },
							isSelected: false,
							imageUrl,
						},
					],
					customInput: prefill,
					artist: item.artist,
					item,
				})
			})
		}
	})

	attachments.forEach((attachment, i) => {
		contentRows.push({
			id: `attachment-${i}`,
			rowType: "attachment",
			pages: [
				{
					id: `attachment-${i}-0`,
					type: { kind: "attachment", attachment },
					isSelected: true,
					imageUrl: null,
				},
			],
			customInput: "",
			artist: attachment.artist,
		})
	})

	return { coverRow, contentRows }
}
